import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import RegistrationProgress from './components/RegistrationProgress';
import PersonalInfoForm from './components/PersonalInfoForm';
import MedicalCredentialsForm from './components/MedicalCredentialsForm';
import DocumentUploadForm from './components/DocumentUploadForm';
import SecuritySetupForm from './components/SecuritySetupForm';
import TrustSignals from './components/TrustSignals';
import { supabase } from '../../lib/supabase';
import { uploadDocument } from '../../services/documentService';

const Registration = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [uploadingDocs, setUploadingDocs] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    // Personal Info
    fullName: '',
    email: '',
    phone: '',
    yearsOfPractice: '',
    // Medical Credentials
    licenseNumber: '',
    licenseState: '',
    specialty: '',
    institution: '',
    institutionType: 'hospital',
    // Documents
    licenseDocument: null,
    affiliationDocument: null,
    // Security
    password: '',
    confirmPassword: '',
    agreeToTerms: false,
    certifyAccuracy: false,
  });

  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleCheckboxChange = (name, checked) => {
    setFormData(prev => ({ ...prev, [name]: checked }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileChange = (name, file) => {
    setFormData(prev => ({ ...prev, [name]: file }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData?.fullName?.trim()) newErrors.fullName = 'Full name is required';
      if (!formData?.email?.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
      if (!formData?.phone?.trim()) newErrors.phone = 'Phone number is required';
      if (!formData?.yearsOfPractice) newErrors.yearsOfPractice = 'Years of practice is required';
    }

    if (step === 2) {
      if (!formData?.licenseNumber?.trim()) {
        newErrors.licenseNumber = 'License number is required';
      } else if (!/^[A-Z]{2}\d{6,}$/?.test(formData?.licenseNumber?.toUpperCase())) {
        newErrors.licenseNumber = 'Invalid license format (e.g., MD123456)';
      }
      if (!formData?.licenseState?.trim()) newErrors.licenseState = 'License issuing location is required';
      if (!formData?.specialty) newErrors.specialty = 'Medical specialty is required';
      if (!formData?.institution?.trim()) newErrors.institution = 'Institution name is required';
    }

    if (step === 3) {
      if (!formData?.licenseDocument) newErrors.licenseDocument = 'Medical license document is required';
      if (!formData?.affiliationDocument) newErrors.affiliationDocument = 'Institutional affiliation proof is required';
    }

    if (step === 4) {
      if (!formData?.password) {
        newErrors.password = 'Password is required';
      } else if (formData?.password?.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])/?.test(formData?.password)) {
        newErrors.password = 'Password must meet all requirements';
      }
      if (!formData?.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (formData?.password !== formData?.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
      if (!formData?.agreeToTerms) newErrors.agreeToTerms = 'You must agree to the terms';
      if (!formData?.certifyAccuracy) newErrors.certifyAccuracy = 'You must certify the accuracy of information';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleRegistrationComplete = async () => {
    try {
      setIsSubmitting(true);
      setError(null);

      // 1. Create user account
      const { data: authData, error: signUpError } = await supabase?.auth?.signUp({
        email: formData?.email,
        password: formData?.password,
        options: {
          data: {
            full_name: formData?.fullName,
            phone_number: formData?.phone,
            specialization: formData?.specialty,
            institution_name: formData?.institution,
            license_number: formData?.licenseNumber
          }
        }
      });

      if (signUpError) throw signUpError;

      const user = authData?.user;
      const userId = user?.id;

      // 2. Upload documents to Supabase storage
      setUploadingDocs(true);
      
      // Upload medical license
      if (formData?.licenseDocument) {
        await uploadDocument(
          formData?.licenseDocument,
          userId,
          'medical_license'
        );
      }

      // Upload clinical affiliation document
      if (formData?.affiliationDocument) {
        await uploadDocument(
          formData?.affiliationDocument,
          userId,
          'clinical_affiliation'
        );
      }

      // Update profile with all information including years of practice
      const { error: profileError } = await supabase?.from('user_profiles')?.update({
          full_name: formData?.fullName,
          phone_number: formData?.phone,
          license_number: formData?.licenseNumber,
          institution_country: formData?.licenseState,
          specialization: formData?.specialty,
          institution_name: formData?.institution,
          institution_type: formData?.institutionType,
          years_of_practice: parseInt(formData?.yearsOfPractice) || null,
          role: 'pending',
          verification_status: 'pending'
        })?.eq('id', userId);

      setUploadingDocs(false);
      setIsSubmitting(false);
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Registration error:', err);
      setError(err?.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateStep(4)) return;

    await handleRegistrationComplete();
  };

  const handleModalClose = () => {
    setShowSuccessModal(false);
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile-optimized header */}
      <div className="fixed top-0 left-0 right-0 h-14 sm:h-16 bg-card border-b border-border z-50 shadow-sm">
        <div className="h-full px-4 sm:px-6 flex items-center justify-between max-w-7xl mx-auto">
          <Link to="/login" className="flex items-center gap-2 sm:gap-3">
            <Image
              src="/public/assets/images/S-2-1765961621758.jpeg"
              alt="My Doc Refer logo - Medical referral platform powered by iRefer guidelines with geometric blue and purple gradient design"
              className="h-8 sm:h-10 w-auto object-contain"
            />
          </Link>
          <Link to="/login" className="text-xs sm:text-sm text-primary hover:text-primary/80 font-medium transition-clinical">
            <span className="hidden sm:inline">Already have an account? </span>Sign In
          </Link>
        </div>
      </div>

      {/* Mobile-optimized content spacing */}
      <div className="pt-16 sm:pt-24 pb-8 sm:pb-12 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          {/* Mobile-optimized title section */}
          <div className="text-center mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">Create Your Account</h1>
            <p className="text-sm sm:text-base text-muted-foreground px-4">Join healthcare professionals using AI-powered diagnostic recommendations</p>
          </div>

          <RegistrationProgress currentStep={currentStep} totalSteps={4} />

          {/* Mobile-optimized grid layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            <div className="lg:col-span-2">
              {uploadError && (
                <div className="bg-error/10 border border-error/20 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
                  <div className="flex items-start gap-2 sm:gap-3">
                    <Icon name="AlertCircle" size={18} className="sm:w-5 sm:h-5 flex-shrink-0" color="var(--color-error)" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs sm:text-sm font-medium text-error">Registration Error</p>
                      <p className="text-xs text-error/80 mt-1 break-words">{uploadError}</p>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                {currentStep === 1 && (
                  <PersonalInfoForm
                    formData={formData}
                    errors={errors}
                    onChange={handleInputChange}
                    onNext={handleNext}
                    savedData={formData}
                  />
                )}

                {currentStep === 2 && (
                  <MedicalCredentialsForm
                    formData={formData}
                    errors={errors}
                    onChange={handleInputChange}
                    onSelectChange={handleSelectChange}
                  />
                )}

                {currentStep === 3 && (
                  <DocumentUploadForm
                    formData={formData}
                    errors={errors}
                    onFileChange={handleFileChange}
                  />
                )}

                {currentStep === 4 && (
                  <SecuritySetupForm
                    formData={formData}
                    errors={errors}
                    onChange={handleInputChange}
                    onCheckboxChange={handleCheckboxChange}
                  />
                )}

                {/* Mobile-optimized button layout */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-0 mt-6 pt-6 border-t border-border">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handlePrevious}
                    disabled={currentStep === 1}
                    iconName="ChevronLeft"
                    iconPosition="left"
                    className="order-2 sm:order-1"
                    fullWidth={false}
                  >
                    <span className="hidden sm:inline">Previous</span>
                    <span className="sm:hidden">Back</span>
                  </Button>

                  {currentStep < 4 ? (
                    <Button
                      type="button"
                      onClick={handleNext}
                      iconName="ChevronRight"
                      iconPosition="right"
                      className="order-1 sm:order-2"
                      fullWidth={false}
                    >
                      <span className="hidden sm:inline">Next Step</span>
                      <span className="sm:hidden">Continue</span>
                    </Button>
                  ) : (
                    <Button
                      type="submit"
                      loading={isSubmitting}
                      disabled={uploadingDocs}
                      iconName="CheckCircle2"
                      iconPosition="left"
                      className="order-1 sm:order-2"
                      fullWidth={false}
                    >
                      {uploadingDocs ? (
                        <>
                          <span className="hidden sm:inline">Uploading Documents...</span>
                          <span className="sm:hidden">Uploading...</span>
                        </>
                      ) : (
                        <>
                          <span className="hidden sm:inline">Submit Registration</span>
                          <span className="sm:hidden">Submit</span>
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </form>
            </div>

            {/* Hide trust signals on mobile, show on large screens */}
            <div className="hidden lg:block lg:col-span-1">
              <TrustSignals />
            </div>
          </div>
        </div>
      </div>

      {/* Mobile-optimized success modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-md p-4 sm:p-6 animate-slide-in max-h-[90vh] overflow-y-auto">
            <div className="text-center">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <Icon name="CheckCircle2" size={24} className="sm:w-8 sm:h-8" color="var(--color-success)" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-foreground mb-2">Registration Submitted!</h3>
              <p className="text-xs sm:text-sm text-muted-foreground mb-4 sm:mb-6 px-2">
                Your registration has been successfully submitted. Our admin team will review your credentials and notify you via email within 24-48 hours.
              </p>
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
                <div className="flex items-start gap-2 sm:gap-3">
                  <Icon name="Mail" size={16} className="sm:w-[18px] sm:h-[18px] flex-shrink-0 mt-0.5" color="var(--color-primary)" />
                  <div className="text-left flex-1 min-w-0">
                    <p className="text-xs sm:text-sm font-medium text-primary">Check Your Email</p>
                    <p className="text-xs text-muted-foreground mt-1 break-words">
                      We've sent a confirmation to <span className="font-medium">{formData?.email}</span>
                    </p>
                  </div>
                </div>
              </div>
              <Button onClick={handleModalClose} fullWidth iconName="ArrowRight" iconPosition="right">
                Return to Login
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Registration;